import { useRecoilCallback, useRecoilValue } from 'recoil';
import type { AnalysisSubject } from 'venn-utils';
import { flattenNode, mapPortfolioIdsToNewPortfolioIds } from 'venn-utils';
import type { Portfolio } from 'venn-api';
import { subjectInputGroups, subjectInputGroupSubjects } from 'venn-state';
import { isNil, mapKeys, omitBy, pickBy } from 'lodash';
import { TreeItemUpdateType } from '../AllocationUtils';
import { useCallback, useState } from 'react';

/**
 * Returns a callback to sync the feesMapping of all subjects in report lab with allocation panel updates
 */
export const useSyncFees = () => {
  const inputGroups = useRecoilValue(subjectInputGroups);

  const [temporaryRemovedFees, setTemporaryRemovedFees] = useState<Map<number, Map<string, number>>>(() => new Map());

  /**
   * Updates feesMapping's for save and save as events.
   * In this case, currentPortfolio and newPortfolio will always have the same structure, but newPortfolio will contain
   * the new portfolio ids after saving.
   */
  const onSavePortfolio = useRecoilCallback(
    ({ set }) =>
      (currentPortfolio: Portfolio, newPortfolio: Portfolio) => {
        const idMap = mapPortfolioIdsToNewPortfolioIds(currentPortfolio, newPortfolio);

        inputGroups.forEach((inputGroup) => {
          set(subjectInputGroupSubjects(inputGroup), (current) =>
            current.map((subject) => {
              if (subject.type !== 'portfolio' || subject.portfolioId !== currentPortfolio.id) {
                return subject;
              }
              const newFeesMapping = omitBy(
                mapKeys(subject.feesMapping, (_value, key) => {
                  return idMap.get(Number(key));
                }),
                (_value, key) => isNil(key),
              );
              return { ...subject, feesMapping: newFeesMapping };
            }),
          );
        });

        setTemporaryRemovedFees(new Map());
      },
    [inputGroups, setTemporaryRemovedFees],
  );

  /**
   * Updates feesMapping's for deletion events (fund or strategy removal).
   * Temporarily stores the removed fees so they can be restored if the user resets.
   */
  const onRemoveItems = useRecoilCallback(
    ({ set }) =>
      (portfolio: Portfolio) => {
        const portfolioNodes = flattenNode(portfolio);
        const portfolioIds = new Set(portfolioNodes.map(({ id }) => id.toString()));

        inputGroups.forEach((inputGroup) => {
          set(subjectInputGroupSubjects(inputGroup), (current) =>
            current.map((subject) => {
              if (subject.type !== 'portfolio' || subject.portfolioId !== portfolio.id) {
                return subject;
              }

              const currentFeesMapping = subject.feesMapping || {};
              const feesToTemporarilyRemove = new Map<string, number>();

              Object.entries(currentFeesMapping).forEach(([portfolioId, feeValue]) => {
                if (!portfolioIds.has(portfolioId)) {
                  feesToTemporarilyRemove.set(portfolioId, feeValue);
                }
              });

              if (feesToTemporarilyRemove.size > 0) {
                setTemporaryRemovedFees((current) => {
                  const newMap = new Map(current);
                  const existingTempRemovedFees = newMap.get(portfolio.id) || new Map();
                  feesToTemporarilyRemove.forEach((value, key) => {
                    existingTempRemovedFees.set(key, value);
                  });
                  newMap.set(portfolio.id, existingTempRemovedFees);
                  return newMap;
                });
              }

              const newFeesMapping = pickBy(currentFeesMapping, (_value, key) => portfolioIds.has(key));
              return { ...subject, feesMapping: newFeesMapping };
            }),
          );
        });
      },
    [inputGroups, setTemporaryRemovedFees],
  );

  /**
   * Restores temporarily removed fees during a reset operation.
   * This ensures that fee exclusions are maintained when subjects are restored.
   */
  const onResetPortfolio = useRecoilCallback(
    ({ set }) =>
      (portfolio: Portfolio) => {
        const tempRemovedFees = temporaryRemovedFees.get(portfolio.id);

        if (!tempRemovedFees || tempRemovedFees.size === 0) {
          return;
        }

        inputGroups.forEach((inputGroup) => {
          set(subjectInputGroupSubjects(inputGroup), (current) =>
            current.map((subject) => {
              if (subject.type !== 'portfolio' || subject.portfolioId !== portfolio.id) {
                return subject;
              }

              const restoredFeesMapping = { ...subject.feesMapping };
              tempRemovedFees.forEach((feeValue: number, portfolioId: string) => {
                restoredFeesMapping[portfolioId] = feeValue;
              });

              return { ...subject, feesMapping: restoredFeesMapping };
            }),
          );
        });

        setTemporaryRemovedFees((current) => {
          const newMap = new Map(current);
          newMap.delete(portfolio.id);
          return newMap;
        });
      },
    [inputGroups, temporaryRemovedFees, setTemporaryRemovedFees],
  );

  const syncFees = useCallback(
    (currentSubject: AnalysisSubject | undefined, newPortfolio: Portfolio, updateType: TreeItemUpdateType) => {
      const currentPortfolio = currentSubject?.portfolio;

      if (!currentPortfolio) return;

      switch (updateType) {
        case TreeItemUpdateType.SAVE:
          onSavePortfolio(currentPortfolio, newPortfolio);
          return;
        case TreeItemUpdateType.RESET:
          onResetPortfolio(newPortfolio);
          return;
        case TreeItemUpdateType.DELETE_PORTFOLIO:
        case TreeItemUpdateType.DELETE_PORTFOLIO_CHILD:
          onRemoveItems(newPortfolio);
      }
    },
    [onSavePortfolio, onRemoveItems, onResetPortfolio],
  );

  return { syncFees };
};
